import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

const PERIODS = [
  { key: 'today',   label: 'Today' },
  { key: 'week',    label: 'This Week' },
  { key: 'month',   label: 'This Month' },
  { key: '3months', label: '3 Months' },
];

function peso(centavos) {
  return (centavos / 100).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function trendPct(current, previous) {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

function TrendBadge({ current, previous }) {
  const pct = trendPct(current, previous);
  if (pct === null) return null;
  const up = pct >= 0;
  return (
    <span className={`text-xs font-medium ${up ? 'text-green-600' : 'text-red-500'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function KpiCard({ label, value, prev, sub }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col gap-1">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-800">₱{value}</p>
      <div className="flex items-center gap-2">
        {prev != null && <TrendBadge current={value.replace(/,/g, '') * 100} previous={prev} />}
        {sub && <span className="text-xs text-gray-400">{sub}</span>}
      </div>
    </div>
  );
}

function KpiCountCard({ label, value, prev, sub }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col gap-1">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      <div className="flex items-center gap-2">
        {prev != null && <TrendBadge current={value} previous={prev} />}
        {sub && <span className="text-xs text-gray-400">{sub}</span>}
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow p-3 text-sm">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name === 'Sales' ? `₱${peso(p.value)}` : `${p.value} orders`}
        </p>
      ))}
    </div>
  );
};

export default function RestaurantDashboard() {
  const { getAccessToken } = useAuth();
  const [period, setPeriod] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const res = await api.get('/restaurant/pos-reports/dashboard', {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: p },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { fetchDashboard(period); }, [period, fetchDashboard]);

  const chartData = data?.salesChart?.map((d) => ({
    name:   d.label,
    Sales:  d.sales,
    Orders: d.orders,
  })) || [];

  const paymentRows = data
    ? Object.entries(data.paymentBreakdown || {}).map(([method, v]) => ({ method, ...v }))
    : [];

  return (
    <Layout pageTitle="Restaurant Dashboard">
      <div className="p-6 bg-gray-50 min-h-screen">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sales Dashboard</h1>
            <p className="text-sm text-gray-500">Restaurant performance overview</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  period === p.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : data ? (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <KpiCard
                label="Gross Sales"
                value={peso(data.grossSales)}
                prev={data.prevGrossSales}
              />
              <KpiCard
                label="Net Sales"
                value={peso(data.netSales)}
                prev={data.prevNetSales}
              />
              <KpiCard
                label="Discount Given"
                value={peso(data.totalDiscount)}
                sub="total discount"
              />
              <KpiCountCard
                label="Orders"
                value={data.totalOrders}
                prev={data.prevTotalOrders}
              />
              <KpiCard
                label="Avg Order Value"
                value={peso(data.avgOrderValue)}
                prev={data.prevAvgOrderValue}
              />
            </div>

            {/* Sales chart */}
            <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
                Sales Over Time
              </h2>
              {chartData.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-10">No sales data for this period</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis
                      yAxisId="sales"
                      tickFormatter={(v) => `₱${(v / 100).toLocaleString()}`}
                      tick={{ fontSize: 11 }}
                      width={72}
                    />
                    <YAxis yAxisId="orders" orientation="right" tick={{ fontSize: 11 }} width={28} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="sales" dataKey="Sales" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                    <Bar yAxisId="orders" dataKey="Orders" fill="#d1d5db" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Top products */}
              <div className="bg-white rounded-xl shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
                  Top Selling Products
                </h2>
                {data.topProducts?.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No sales data</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 uppercase border-b">
                        <th className="text-left pb-2 font-medium">#</th>
                        <th className="text-left pb-2 font-medium">Product</th>
                        <th className="text-right pb-2 font-medium">Qty</th>
                        <th className="text-right pb-2 font-medium">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topProducts.map((p, i) => (
                        <tr key={p.name} className="border-b last:border-0">
                          <td className="py-2 text-gray-400">{i + 1}</td>
                          <td className="py-2 font-medium text-gray-800">{p.name}</td>
                          <td className="py-2 text-right text-gray-600">{p.quantity}</td>
                          <td className="py-2 text-right text-gray-800">₱{peso(p.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Payment breakdown */}
              <div className="bg-white rounded-xl shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
                  Payment Breakdown
                </h2>
                {paymentRows.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No payments</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 uppercase border-b">
                        <th className="text-left pb-2 font-medium">Method</th>
                        <th className="text-right pb-2 font-medium">Count</th>
                        <th className="text-right pb-2 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentRows.map((row) => (
                        <tr key={row.method} className="border-b last:border-0">
                          <td className="py-2 font-medium text-gray-800 capitalize">{row.method}</td>
                          <td className="py-2 text-right text-gray-600">{row.count}</td>
                          <td className="py-2 text-right text-gray-800">₱{peso(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Low stock placeholder */}
            <div className="bg-white rounded-xl shadow-sm p-5 border border-dashed border-gray-200">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
                Low Stock Alerts — coming soon
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                Once inventory deduction is enabled for compound products, low-stock alerts will appear here.
              </p>
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
}
